import type { AutosuggestProps } from '@risk-smart/themed-cloudscape-components/autosuggest';
import { useState } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import Autosuggest from 'src/components/form/autosuggest';
import { FormField } from 'src/components/form/form/FormField';
import type { Content } from 'src/components/help-panel/useHelpStore';
import Tokens from 'src/components/tokens';
import type {
  IssueAssessmentFields,
  ResponsibleThirdPartyToken,
} from 'src/pages/issues/update/forms/issue-assessment-form/issueAssessmentSchema';

import { thirdPartyDetailsUrl } from '@/utils/urls';

export interface ResponsibleThirdPartyAutosuggestViewProps {
  disabled: boolean;
  errorText?: string;
  field: ControllerRenderProps<IssueAssessmentFields, 'ThirdPartyResponsible'>;
  guidance?: Content;
  label: string;
  options: AutosuggestProps.Options | undefined;
  placeholder?: string;
  statusType: AutosuggestProps['statusType'];
  testId?: string;
}

const tokenKey = (token: ResponsibleThirdPartyToken): string =>
  `${token.kind}:${token.value}`;

export const ResponsibleThirdPartyAutosuggestView = ({
  disabled,
  errorText,
  field: { ref, name, onBlur, onChange, value },
  guidance,
  label,
  options,
  placeholder,
  statusType,
  testId,
}: ResponsibleThirdPartyAutosuggestViewProps) => {
  const [currentValue, setCurrentValue] = useState('');
  const selectedTokens = value ?? [];

  const addToken = (selectedValue: string) => {
    const registeredOption = options?.find(
      (option) =>
        'value' in option && option.value === selectedValue && option.label
    );
    const trimmedValue = selectedValue.trim();
    if (!registeredOption && !trimmedValue) {
      return;
    }

    const token: ResponsibleThirdPartyToken = registeredOption
      ? {
          kind: 'registered',
          value: String(registeredOption.value),
          label: String(registeredOption.label),
        }
      : {
          kind: 'adHoc',
          value: trimmedValue,
          label: trimmedValue,
        };

    if (
      !selectedTokens.some((existing) => tokenKey(existing) === tokenKey(token))
    ) {
      onChange([...selectedTokens, token]);
    }
    setCurrentValue('');
  };

  return (
    <FormField
      label={label}
      testId={testId}
      errorText={errorText}
      stretch
      guidance={guidance}
      previewChangesFormatter={(tokens: ResponsibleThirdPartyToken[]) =>
        tokens.map((token) => token.label).join(', ')
      }
    >
      <Autosuggest
        ref={ref}
        name={name}
        value={currentValue}
        onBlur={onBlur}
        onKeyDown={(event) => {
          if (event.detail.key === 'Enter' && currentValue) {
            addToken(currentValue);
          }
        }}
        onSelect={(event) => addToken(event.detail.value)}
        onChange={(event) => setCurrentValue(event.detail.value)}
        options={options}
        statusType={statusType}
        enteredTextLabel={(enteredValue) => `Use: "${enteredValue}"`}
        ariaLabel={'Enter value'}
        placeholder={placeholder ?? 'Enter value'}
        empty={'No matches found'}
        disabled={disabled}
      />
      <Tokens
        disabled={disabled}
        onRemove={(key) =>
          onChange(selectedTokens.filter((token) => tokenKey(token) !== key))
        }
        tokens={selectedTokens.map((token) => ({
          value: tokenKey(token),
          label: token.label,
          url:
            token.kind === 'registered'
              ? thirdPartyDetailsUrl(token.value)
              : undefined,
        }))}
      />
    </FormField>
  );
};
